'use client'
import { MemberOutcomeSection as MemberOutcomeTool } from '@achieve/cx-member-outcomes'
import Section from 'components/Section/Section'
import styles from './MemberOutcomeSection.module.scss'
import { useLazyQuery, gql } from '@apollo/client'
import { useMemoizedContentGetter } from 'utils/contentful'
import { TitleDivider } from 'components/TitleDivider'
import { useRef, useState } from 'react'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { AchieveTheme as theme } from '@achieve/ascend'
import { useTrackLoadEvent } from 'hooks/useTrackLoadEvent'
import { mainProductConfig } from 'constants/mainProductConfig'
import ApolloClientProvider from 'providers/ApolloClientProvider'

export const GET_MEMBER_EXAMPLE = gql`
  query memberExample($filter: MemberExampleFilter) {
    memberExampleCollection(order: totalCostDiscountSavings_DESC, where: $filter) {
      items {
        name
        gender
        associatedProduct
        enrolledDebtAmount
        numberOfEnrolledDebts
        loanAmount
        monthlyPaymentBeforeDiscount
        monthlyPayment
        monthlyPaymentDiscountSavings
        aprBeforeDiscount
        apr
        totalInterestAndFeesBeforeDiscount
        totalInterestAndFees
        totalInterestAndFeesDiscountSavings
        totalCostBeforeDiscount
        totalCost
        totalCostDiscountSavings
        term
        permissionToUsePii
        image {
          url
          description
          height
          width
        }
      }
    }
  }
`

export const productTypeMapping = {
  'personal loan': 'Personal Loan',
  pl: 'Personal Loan',
  'debt relief': 'Debt Relief',
  dr: 'Debt Relief',
  'home loan': 'Home Loan',
  hl: 'Home Loan',
  'acceleration loan': 'Acceleration Loan',
  al: 'Acceleration Loan',
}

const getClosestMemberByDebtAmount = (members = [], enrolledDebtAmount) => {
  return members.reduce(
    (closestMember, member) => {
      const difference = Math.abs(enrolledDebtAmount - member.enrolledDebtAmount)
      return difference < closestMember.difference ? { member, difference } : closestMember
    },
    { member: null, difference: Infinity }
  ).member
}

function MemberOutcomeSectionModule({ content, fetchMemberExampleByDebt }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const trackRefCta = useRef()
  const trackRefSlider = useRef()
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  let additionalConfiguration = content?.fields?.additionalConfiguration?.jsonContent || {}

  const backgroundColorMap = {
    neutral0: theme.ascend?.colors?.neutral?.[0],
    neutral25: theme.ascend?.colors?.neutral?.[25],
    blue25: theme.ascend?.colors.blue?.[25],
    blue100: theme.ascend?.colors?.blue?.[100],
  }

  const { title } = useMemoizedContentGetter(content, ['title'])

  const [sliderSelectedValue, setSliderSelectedValue] = useState(
    additionalConfiguration?.defaultSliderValue || 0
  )

  useTrackLoadEvent({
    event_action: 'app_event',
    form_ss_amount: `${sliderSelectedValue}`,
    track_event: 'tool_render',
  })

  const args = {
    ...additionalConfiguration,
    onCtaClick: function () {
      trackRefCta?.current?.click()
    },
    fetchMemberExample: fetchMemberExampleByDebt,
    onApiCall: function (sliderSelectedValue) {
      setSliderSelectedValue(sliderSelectedValue)
    },
    onChangeValue: function () {
      trackRefSlider?.current?.click()
    },
  }

  return (
    <Section
      backgroundColor={
        backgroundColorMap[additionalConfiguration?.backgroundColor] ?? backgroundColorMap.neutral25
      }
      className={styles['container']}
    >
      <TitleDivider title={title} />
      <MemberOutcomeTool {...args} />
      <TrackingWrapper
        data-testid="member-outcome-cta-tracking-wrapper"
        track={{
          list_name: 'MEMBER OUTCOME CTA',
          click_id: additionalConfiguration?.memberOutcomeCustomerProps?.ctaCopy,
          click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
            additionalConfiguration?.memberOutcomeCustomerProps?.ctaCopy
          }`,
          click_url: additionalConfiguration?.memberOutcomeCustomerProps?.ctaUrl,
          sectionVariationObject,
        }}
      >
        <div ref={trackRefCta}></div>
      </TrackingWrapper>
      <TrackingWrapper
        data-testid="member-outcome-slider-tracking-wrapper"
        track={{
          list_name: 'MEMBER OUTCOME SLIDER',
          click_id: sliderSelectedValue,
          click_text: `${
            mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
          } | ${sliderSelectedValue}`,
          sectionVariationObject,
        }}
      >
        <div ref={trackRefSlider}></div>
      </TrackingWrapper>
    </Section>
  )
}

function MemberOutcomeSectionApollo({ content }) {
  const additionalConfiguration = content?.fields?.additionalConfiguration?.jsonContent || {}
  const [fetchMemberExample] = useLazyQuery(GET_MEMBER_EXAMPLE, {
    variables: {
      filter: {
        associatedProduct:
          productTypeMapping[
            (additionalConfiguration?.productType || 'Personal Loan').toLowerCase()
          ],
      },
    },
  })

  const fetchAndFilterMemberExampleByEnrolledDebt = async (enrolledDebtAmount) => {
    const { data } = await fetchMemberExample()
    return getClosestMemberByDebtAmount(
      data?.memberExampleCollection?.items || [],
      enrolledDebtAmount
    )
  }

  return (
    <MemberOutcomeSectionModule
      content={content}
      fetchMemberExampleByDebt={fetchAndFilterMemberExampleByEnrolledDebt}
    />
  )
}

function MemberOutcomeSection({ content }) {
  const initialMemberExamples = content?.fields?.initialMemberExamples || []
  const hasServerData = initialMemberExamples.length > 0

  if (hasServerData) {
    const fetchFromServerSeededExamples = async (enrolledDebtAmount) =>
      getClosestMemberByDebtAmount(initialMemberExamples, enrolledDebtAmount)

    return (
      <MemberOutcomeSectionModule
        content={content}
        fetchMemberExampleByDebt={fetchFromServerSeededExamples}
      />
    )
  }

  return (
    <ApolloClientProvider>
      <MemberOutcomeSectionApollo content={content} />
    </ApolloClientProvider>
  )
}

export { MemberOutcomeSection }
export default MemberOutcomeSection
